<template>
  <UCPreload
    :show="isLoadingConsumptions"
    :number="1"
  />

  <div
    v-if="!isLoadingConsumptions"
    class="container-table"
  >
    <table>
      <!-- Table header -->
      <thead>
        <tr>
          <th
            scope="col"
            class="padding-left-half"
          >
            {{ $t('common.warehouse') }}
          </th>
          <th scope="col">
            {{ $t('common.article') }}
          </th>
          <th scope="col">
            {{ $t('common.name') }}
          </th>
          <th scope="col">
            {{ $t('common.quantity') }}
          </th>
          <template v-if="!isPartFinished">
            <!-- Edit quantity -->
            <th
              v-if="shouldModifyArticleQuantityBeDisplayed"
              scope="col"
            />
            <!-- Delete -->
            <th scope="col" />
          </template>
        </tr>
      </thead>

      <!-- Table data -->
      <tbody>
        <template v-if="realConsumptions && realConsumptions.length > 0">
          <tr
            v-for="(item, i) in realConsumptions"
            :key="i"
          >
            <td class="text-align-center">
              {{ item.Almacen ? item.Almacen.Nombre : '' }}
            </td>

            <td class="text-align-center">
              {{ item.Articulo ? item.Articulo.Codigo : '' }}
            </td>

            <td class="text-align-center">
              {{ item.Articulo ? item.Articulo.Nombre : '' }}
              <template v-if="item.Observaciones">
                <br>
                ({{ item.Observaciones }})
              </template>
            </td>

            <td class="text-align-center">
              {{ item.Cantidad }}
            </td>

            <template v-if="!isPartFinished">
              <!-- Edit quantity -->
              <td
                v-if="shouldModifyArticleQuantityBeDisplayed"
                class="text-align-center"
              >
                <f7-button
                  v-if="shouldDisplayEditButtonForArticle(item)"
                  title="Editar cantidad"
                  @click="handleArticleEdit(item)"
                >
                  <UCSvgIcon name="fa6-solid-pen" />
                </f7-button>
              </td>

              <!-- Delete -->
              <td class="text-align-center">
                <f7-button
                  :preloader="isDeletingArticle[item.Id]"
                  :loading="isDeletingArticle[item.Id]"
                  :disabled="isDeletingArticle[item.Id]"
                  title="Borrar artículo"
                  @click="handleArticleDelete(item, i)"
                >
                  <UCSvgIcon
                    name="fa6-solid-trash"
                    color="var(--his-tomate)"
                  />
                </f7-button>
              </td>
            </template>
          </tr>
        </template>

        <!-- No data to show -->
        <template v-else>
          <tr>
            <td
              :colspan="colspan"
              class="text-align-center"
            >
              No hay datos
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <PCPartsDetailsCardArticlesRealQuantityActions
      v-model:opened="isSelectQuantityActionsOpened"
      :selected-article="selectedArticle"
      @success="onArticleQuantityModification"
    />
  </div>
</template>

<script setup>
// @ts-check

import {
  computed,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRepairParts } from '@/composables/useRepairParts';
import { useParamRef } from '@erpagroapp/params';

import { logError } from '@/js/utils/log';
import { callDeleteArticle, callFetchRealConsumptions } from '@/js/services/repairs/ApiRepairs';
import { ConfirmDialogGH, HiNotification } from '@/js/common/HINotificationSupport';

import UCPreload from '@/components/common/UCPreload.vue';
import UCSvgIcon from '@/components/common/UCSvgIcon.vue';
import PCPartsDetailsCardArticlesRealQuantityActions from './PCPartsDetailsCardArticlesRealQuantityActions.vue';

/** @import { Ref } from 'vue' */

/** @import { ParteProduccionRealEntrada } from '@erpagroapp/types' */

const isLoadingConsumptions = ref(true);

/** @type {Ref<Record<number, boolean>>} */
const isDeletingArticle = ref({});

/** @type {Ref<ParteProduccionRealEntrada[]>} */
const realConsumptions = ref([]);

/** @type {Ref<ParteProduccionRealEntrada | undefined>} */
const selectedArticle = ref(undefined);

/** @type {Ref<boolean> } */
const isSelectQuantityActionsOpened = ref(false);

const { workPartDetails } = useRepairParts();

const partStatusFinished = useParamRef('EstadoFinalizacionDefectoParteReparacion');
const consumedInterventionsArticle = useParamRef('ArticuloConsumoReparacion');
const consumedDisplacementArticle = useParamRef('ArticuloConsumoDesplazamiento');
const isAllowedConsumedArticlesQuantityModification = useParamRef('PermitirModificarCantidadArticulosConsumidosReparaciones');

const isPartFinished = computed(() => !!workPartDetails.value
  && !!workPartDetails.value.Codigo_ParteProduccionEstado
  && workPartDetails.value.Codigo_ParteProduccionEstado === partStatusFinished.value);

const colspan = computed(() => (isPartFinished.value ? 5 : 6));

/**
 * Determines if the edit button should be displayed for the article.
 * @param {ParteProduccionRealEntrada} item - The article item.
 * @returns {boolean} `true` if the edit button should be displayed, `false` otherwise.
 */
function shouldDisplayEditButtonForArticle(item) {
  if (!item || !item.Articulo) {
    return false;
  }

  return isAllowedConsumedArticlesQuantityModification.value
    || (item.Articulo.Codigo === consumedInterventionsArticle.value
      || item.Articulo.Codigo === consumedDisplacementArticle.value);
}

const shouldModifyArticleQuantityBeDisplayed = computed(
  () => isAllowedConsumedArticlesQuantityModification.value
    || realConsumptions.value.some((item) => shouldDisplayEditButtonForArticle(item)),
);

/**
 * Fetches expected consumption articles.
 * @param {number} workPartId - The ID of the work part.
 */
async function fetchRealConsumptions(workPartId) {
  try {
    const { data } = await callFetchRealConsumptions(workPartId);
    realConsumptions.value = data;
  } catch (error) {
    logError(error);
  } finally {
    isLoadingConsumptions.value = false;
  }
}

/**
 * Deletes an article.
 * @param {ParteProduccionRealEntrada} item - The article item.
 */
async function deleteArticle(item) {
  let successful = false;
  try {
    isDeletingArticle.value[item.Id] = true;
    const { data } = await callDeleteArticle(item);

    if (data.Valor === true) {
      successful = true;
      HiNotification.Toast('El artículo se ha borrado correctamente');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido borrar el artículo del parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido borrar el artículo del parte');
  } finally {
    isDeletingArticle.value[item.Id] = false;
  }

  return successful;
}

/**
 * Deletes an article.
 * @param {ParteProduccionRealEntrada} item - The article item.
 * @param {number} index - The index of the article in the list.
 */
async function handleArticleDelete(item, index) {
  if (!item.Articulo) {
    return;
  }

  const resultConfirm = await ConfirmDialogGH(
    `Esta acción borrará el artículo <strong>${item.Articulo.Codigo}/${item.Articulo.Nombre}</strong>`,
    '¿Quiere continuar?',
  );

  if (resultConfirm) {
    const result = await deleteArticle(item);

    if (result) {
      // By updating the prop directly, there is no need to reload the component
      realConsumptions.value.splice(index, 1);
    }
  }
}

/**
 * Handles the article edit.
 * @param {ParteProduccionRealEntrada} article - The article to edit.
 */
async function handleArticleEdit(article) {
  selectedArticle.value = article;

  await nextTick();
  isSelectQuantityActionsOpened.value = true;
}

function onArticleQuantityModification() {
  if (!workPartDetails.value) {
    return;
  }

  fetchRealConsumptions(workPartDetails.value.Id_ParteProduccion);
}

watch(workPartDetails, () => {
  if (workPartDetails.value) {
    fetchRealConsumptions(workPartDetails.value.Id_ParteProduccion);
  }
});

onMounted(() => {
  if (!workPartDetails.value) {
    return;
  }

  fetchRealConsumptions(workPartDetails.value.Id_ParteProduccion);
});
</script>

<style scoped lang="scss">
.container-table {
  overflow-x: auto;
  padding: calc(var(--f7-typography-padding) / 2);

  table {
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;

    thead {
      background-color: #9e9e9e;

      th {
        background-color: #9e9e9e;
        padding: 5px;
      }

      th:first-child {
        padding-left: calc(var(--f7-typography-padding) / 2);
        border-top-left-radius: 5px;
      }

      th:last-child {
        padding-right: calc(var(--f7-typography-padding) / 2);
        border-top-right-radius: 5px;
      }
    }

    tbody {
      background-color: #e6e6e6;

      td {
        padding: 5px;
      }

      td:first-child {
        padding-left: calc(var(--f7-typography-padding) / 2);
      }

      td:last-child {
        padding-right: calc(var(--f7-typography-padding) / 2);
      }
    }
  }
}
</style>
